<template>
    <modal size="large" :no-footer="true" @close="$emit('close')">
        <template slot="header">
            <strong>Edit a cycle</strong>
        </template>

        <template slot="content">
            <form @submit.prevent="onSubmit">
                <div class="flex jc-sb">
                    <div class="flex-50 mr-2" data-cy="facility-number-field">
                        <label>
                            Facility Number:<br />
                            <input disabled type="text" :value="inputs.facility_number" />
                        </label>
                    </div>

                    <div class="flex-50 ml-2" data-cy="label-field">
                        <label>
                            Label:<br />
                            <input :disabled="isLoading" type="text" required v-model="inputs.label" />
                        </label>
                    </div>
                </div>

                <div class="flex jc-sb mt-4">
                    <div class="flex-33 mr-2" data-cy="initial-letter-date-field">
                        <label>
                            Initial Letter Date:<br />
                            <datepicker :disabled="isLoading" :typeable="true" v-model="inputs.initial_letter_send_date" format="MM/dd/yyyy" />
                        </label>
                    </div>

                    <div class="flex-33 ml-2 mr-2" data-cy="reminder-letter-date-field">
                        <label>
                            Reminder Letter Date:<br />
                            <datepicker :disabled="isLoading" :typeable="true" v-model="inputs.reminder_letter_send_date" format="MM/dd/yyyy" />
                        </label>
                    </div>

                    <div class="flex-33 ml-2" data-cy="letter-effective-date-field">
                        <label>
                            Letter Effective Date:<br />
                            <datepicker :disabled="isLoading" :typeable="true" v-model="inputs.letter_effective_date" format="MM/dd/yyyy" />
                        </label>
                    </div>
                </div>

                <div class="flex jc-sb mt-4">
                    <div class="flex-50 mr-2">
                        <label>
                            Update Responses Deadline Date:<br />
                            <datepicker
                                id="edit-cycle-update-responses-deadline-date"
                                :disabled="isLoading"
                                :typeable="true"
                                v-model="inputs.update_responses_deadline_date"
                                format="MM/dd/yyyy" />
                        </label>
                    </div>

                    <div class="flex-50 ml-2">
                        <label>
                            Tag for Removal Deadline Date:<br />
                            <datepicker
                                id="edit-cycle-tag-for-removal-deadline-date"
                                :disabled="isLoading"
                                :typeable="true"
                                v-model="inputs.tag_for_removal_deadline_date"
                                format="MM/dd/yyyy" />
                        </label>
                    </div>
                </div>

                <div class="flex jc-sb mt-4">
                    <div class="flex-50 mr-2">
                        <label>
                            Setup Coverage Dates:<br />
                            <div class="flex ai-c jc-sb">
                                <datepicker
                                    id="edit-cycle-setup-coverage-start-date"
                                    :disabled="isLoading"
                                    :typeable="true"
                                    v-model="inputs.setup_coverage_start_date"
                                    format="MM/dd/yyyy" />

                                <span class="ml-2 mr-2">&ndash;</span>

                                <datepicker
                                    id="edit-cycle-setup-coverage-end-date"
                                    :disabled="isLoading"
                                    :typeable="true"
                                    v-model="inputs.setup_coverage_end_date"
                                    format="MM/dd/yyyy" />
                            </div>
                        </label>
                    </div>

                    <div class="flex-50 ml-2">
                        <label>
                            Monitor for Payment Dates:<br />
                            <div class="flex ai-c jc-sb">
                                <datepicker
                                    id="edit-cycle-monitor-payment-start-date"
                                    :disabled="isLoading"
                                    :typeable="true"
                                    v-model="inputs.monitor_payment_start_date"
                                    format="MM/dd/yyyy" />

                                <span class="ml-2 mr-2">&ndash;</span>

                                <datepicker
                                    id="edit-cycle-monitor-payment-end-date"
                                    :disabled="isLoading"
                                    :typeable="true"
                                    v-model="inputs.monitor_payment_end_date"
                                    format="MM/dd/yyyy" />
                            </div>
                        </label>
                    </div>
                </div>

                <div class="flex jc-sb mt-5">
                    <div class="flex-a mr-2">
                        <span class="danger-text">{{ errorMessage }}</span>
                    </div>

                    <div class="flex-0 flex jc-fe ml-2">
                        <div v-show="isLoading" class="flex ai-c mr-2">
                            <img src="@/assets/img/loading-black-32.gif" alt="loading" style="max-width: none;" />
                        </div>

                        <button :disabled="isLoading" type="submit">Save</button>
                    </div>
                </div>
            </form>
        </template>
    </modal>
</template>

<script>
import http from "@/http"
import dayjs from "dayjs"
import { objectToParams } from "@/utilities/form-data"

export default {
    name: "edit-cycle-modal",

    props: {
        cycle: {
            required: true,
            type: Object
        },

        onSuccess: {
            type: Function
        }
    },

    data () {
        let inputs = Object.assign({}, this.cycle)

        if (inputs.initial_letter_send_date !== null) {
            inputs.initial_letter_send_date = dayjs(inputs.initial_letter_send_date).toDate()
        } else {
            inputs.initial_letter_send_date = null
        }

        if (inputs.reminder_letter_send_date !== null) {
            inputs.reminder_letter_send_date = dayjs(inputs.reminder_letter_send_date).toDate()
        } else {
            inputs.reminder_letter_send_date = null
        }

        if (inputs.letter_effective_date !== null) {
            inputs.letter_effective_date = dayjs(inputs.letter_effective_date).toDate()
        } else {
            inputs.letter_effective_date = null
        }

        if (inputs.update_responses_deadline_date !== null) {
            inputs.update_responses_deadline_date = dayjs(inputs.update_responses_deadline_date).toDate()
        } else {
            inputs.update_responses_deadline_date = null
        }

        if (inputs.tag_for_removal_deadline_date !== null) {
            inputs.tag_for_removal_deadline_date = dayjs(inputs.tag_for_removal_deadline_date).toDate()
        } else {
            inputs.tag_for_removal_deadline_date = null
        }

        if (inputs.setup_coverage_start_date !== null) {
            inputs.setup_coverage_start_date = dayjs(inputs.setup_coverage_start_date).toDate()
        } else {
            inputs.setup_coverage_start_date = null
        }

        if (inputs.setup_coverage_end_date !== null) {
            inputs.setup_coverage_end_date = dayjs(inputs.setup_coverage_end_date).toDate()
        } else {
            inputs.setup_coverage_end_date = null
        }

        if (inputs.monitor_payment_start_date !== null) {
            inputs.monitor_payment_start_date = dayjs(inputs.monitor_payment_start_date).toDate()
        } else {
            inputs.monitor_payment_start_date = null
        }

        if (inputs.monitor_payment_end_date !== null) {
            inputs.monitor_payment_end_date = dayjs(inputs.monitor_payment_end_date).toDate()
        } else {
            inputs.monitor_payment_end_date = null
        }

        return {
            errorMessage: "",
            inputs,
            isLoading: false
        }
    },

    methods: {
        /**
         * Make the API call to create the cycle.
         *
         * @return null
         */
        async onSubmit () {
            const params = objectToParams(this.inputs)

            this.isLoading = true

            try {
                const { data } = await http.post(`/api/cycles/${this.cycle.id}`, params)

                if (this.onSuccess) {
                    this.onSuccess(data.cycle_id)
                }

                this.$emit("close")
            } catch (ex) {
                this.errorMessage = ex
            } finally {
                this.isLoading = false
            }
        }
    }
}
</script>

<style scoped>
>>> .modal-body {
    overflow: visible;
}
</style>



// WEBPACK FOOTER //
// src/components/manage-cycles-page/EditCycleModal.vue